"use client";

import {
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { FLEISS_KAPPA_CALCULATOR_STRINGS } from "@/components/tools/fleiss-kappa-calculator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type AgreementBand,
  type FleissResult,
  fleissKappa,
  landisKoch,
} from "@/lib/tool-kit/stats-research/agreement";
import {
  apaBounded,
  apaPClause,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of the Fleiss' kappa calculator. Strings are the widget's own.
 */

type Strings = typeof FLEISS_KAPPA_CALCULATOR_STRINGS;

const BAND_KEY: Record<AgreementBand, keyof Strings> = {
  poor: "bandPoor",
  slight: "bandSlight",
  fair: "bandFair",
  moderate: "bandModerate",
  substantial: "bandSubstantial",
  almostPerfect: "bandAlmostPerfect",
};

function CategoryTable({
  result,
  categories,
  strings,
  fmt,
}: {
  result: FleissResult;
  categories: string[];
  strings: Strings;
  fmt: Formatter;
}) {
  const head = [
    strings.columnCategory,
    strings.columnShare,
    strings.columnKappa,
    strings.columnZ,
    strings.columnP,
  ];
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((category, j) => {
            const kappa = result.categoryKappa[j];
            const defined = Number.isFinite(kappa);
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: categories are positional
              <tr key={j}>
                <th className={`${TD} font-medium`} scope="row">
                  {category}
                </th>
                <td className={`${TD} font-mono`}>
                  {fixed(fmt, result.proportions[j], 3)}
                </td>
                <td className={`${TD} font-mono`}>
                  {defined ? fixed(fmt, kappa, 3) : strings.undefinedCategory}
                </td>
                <td className={`${TD} font-mono`}>
                  {defined ? fixed(fmt, result.categoryZ[j], 2) : "—"}
                </td>
                <td className={`${TD} font-mono`}>
                  {defined ? formatP(fmt, result.categoryP[j]) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Working({
  result,
  strings,
  fmt,
}: {
  result: FleissResult;
  strings: Strings;
  fmt: Formatter;
}) {
  const four = (value: number) => fixed(fmt, value, 4);
  const terms = result.proportions
    .map((p) => `${fmt(p, { maximumFractionDigits: 4 })}²`)
    .join(" + ");
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingSubject, { pBar: four(result.meanAgreement) })}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingChance, {
          terms,
          peBar: four(result.chanceAgreement),
        })}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingKappa, {
          pBar: four(result.meanAgreement),
          peBar: four(result.chanceAgreement),
          kappa: fixed(fmt, result.kappa, 3),
        })}
      </p>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.workingZ, {
          kappa: fixed(fmt, result.kappa, 4),
          se: four(result.seNull),
          z: fixed(fmt, result.z, 2),
        })}
      </p>
    </div>
  );
}

export function FleissSummary({
  counts,
  categories,
  strings,
  fmt,
  locale,
}: {
  counts: number[][];
  categories: string[];
  strings: Strings;
  fmt: Formatter;
  locale: string;
}) {
  const result = fleissKappa(counts);
  if (!result) {
    const raters = counts[0]?.reduce((sum, value) => sum + value, 0) ?? 0;
    return (
      <Notice>
        {raters >= 2 && categories.length >= 2
          ? strings.degenerate
          : strings.tooFew}
      </Notice>
    );
  }
  const three = (value: number) => fixed(fmt, value, 3);
  const report = t(strings.report, {
    kappa: apaBounded(fmt, result.kappa),
    z: fixed(fmt, result.z, 2),
    p: apaPClause(fmt, result.p),
    subjects: tPlural(strings.subjectCount, locale, result.subjects, {
      count: fmt(result.subjects),
    }).trim(),
    raters: tPlural(strings.raterCount, locale, result.raters, {
      count: fmt(result.raters),
    }).trim(),
  });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.kappa} value={three(result.kappa)} />
        <StatCard
          label={strings.meanAgreement}
          value={three(result.meanAgreement)}
        />
        <StatCard
          label={strings.chanceAgreement}
          value={three(result.chanceAgreement)}
        />
        <StatCard label={strings.seNull} value={fixed(fmt, result.seNull, 4)} />
        <StatCard label={strings.zLabel} value={fixed(fmt, result.z, 2)} />
        <StatCard label={strings.pLabel} value={formatP(fmt, result.p)} />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.interpretation, {
          kappa: three(result.kappa),
          band: strings[BAND_KEY[landisKoch(result.kappa)]],
        })}{" "}
        {strings.seNote}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <p className="font-mono text-muted-foreground text-sm">{report}</p>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.categoryHeading}</h3>
        <CategoryTable
          categories={categories}
          fmt={fmt}
          result={result}
          strings={strings}
        />
        <p className="text-muted-foreground text-xs">
          {t(strings.categoryNote, {
            se: fixed(fmt, result.categorySeNull, 4),
          })}
        </p>
      </div>
      <Working fmt={fmt} result={result} strings={strings} />
    </section>
  );
}
